import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router";
import {
  useCities,
  useSaveTargetOptions,
  useStates,
  useSurveyDetail,
  useTargetMetaOptions,
  useTargetOptions,
} from "./api";
import {
  GENDER_OPTIONS,
  META_FIELDS,
  citiesForStates,
  hasAnyCriterion,
  isFieldVisible,
  selectedAudiences,
  withDefaults,
  type MetaOption,
  type TargetCriteria,
  type TargetOptions,
  type TargetType,
} from "./targeting";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useNotify } from "../../app/useNotify";
import { usePageTitle } from "../../app/usePageTitle";
import { errorMessageFor } from "../../api/client";

/** The date inputs speak `YYYY-MM-DD`; the server speaks a full timestamp. */
function toDateInput(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

interface Draft {
  targetType: TargetType;
  startDate: string;
  endDate: string;
  criteria: TargetCriteria;
}

function draftFrom(options: TargetOptions | undefined): Draft {
  return {
    targetType: options?.targetType === "CRITERIA" ? "CRITERIA" : "ALL",
    startDate: toDateInput(options?.startDate),
    endDate: toDateInput(options?.endDate),
    criteria: withDefaults(options?.criteria),
  };
}

export function SurveyTargetPage() {
  const { surveyId: param } = useParams();
  const surveyId = Number(param);
  const valid = Number.isFinite(surveyId);
  const { can } = useAuth();
  const notify = useNotify();

  const detail = useSurveyDetail(valid ? surveyId : null);
  usePageTitle(detail.data?.surveyName ?? "Survey");
  const target = useTargetOptions(valid ? surveyId : null);
  const metas = useTargetMetaOptions();
  const states = useStates();
  const cities = useCities();
  const save = useSaveTargetOptions(surveyId);

  const [edited, setEdited] = useState<Draft | null>(null);
  const { targetType, startDate, endDate, criteria } = edited ?? draftFrom(target.data);
  const update = (patch: Partial<Draft>) =>
    setEdited({ ...(edited ?? draftFrom(target.data)), ...patch });

  const userTypeOptions = metas.data?.userType;
  const audiences = useMemo(
    () => selectedAudiences(criteria.userType, userTypeOptions ?? []),
    [criteria.userType, userTypeOptions],
  );
  const cityOptions = useMemo(
    () => citiesForStates(cities.data ?? [], criteria.state),
    [cities.data, criteria.state],
  );

  const locked = (detail.data?.status ?? "PENDING") !== "PENDING";
  const editable = can(Authority.EditSurvey) && !locked;

  const setCriterion = (key: keyof TargetCriteria, value: number[] | string) =>
    update({ criteria: { ...criteria, [key]: value } });

  const datesMissing = startDate === "" || endDate === "";
  const endsBeforeStart = !datesMissing && endDate < startDate;
  const criteriaEmpty = targetType === "CRITERIA" && !hasAnyCriterion(criteria);
  const blocked = datesMissing || endsBeforeStart || criteriaEmpty;

  async function onSave() {
    try {
      await save.mutateAsync({
        startDate: `${startDate} 00:00:00`,
        endDate: `${endDate} 23:59:59`,
        targetType,
        criteria,
      });
      notify.success("Targeting saved");
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not save targeting");
    }
  }

  if (!valid) return <Alert severity="error">That survey id is not a number.</Alert>;
  if (target.isLoading || detail.isLoading || metas.isLoading) return <CircularProgress />;
  if (target.isError)
    return <Alert severity="error">Could not load this survey's targeting.</Alert>;

  const multiSelect = (key: keyof TargetCriteria, label: string, options: MetaOption[]) => {
    const value = (criteria[key] as number[]) ?? [];
    const labelId = `target-${String(key)}`;
    return (
      <FormControl key={String(key)} sx={{ minWidth: 260 }} size="small" disabled={!editable}>
        <InputLabel id={`${labelId}-label`}>{label}</InputLabel>
        <Select
          labelId={`${labelId}-label`}
          id={labelId}
          multiple
          value={value}
          input={<OutlinedInput label={label} />}
          onChange={(event) => setCriterion(key, event.target.value as number[])}
          renderValue={(selected) => (
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
              {(selected as number[]).map((id) => (
                <Chip key={id} size="small" label={options.find((o) => o.id === id)?.label ?? id} />
              ))}
            </Stack>
          )}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              <Checkbox checked={value.includes(option.id)} size="small" />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">Targeting</Typography>
        <Typography variant="body2" color="text.secondary">
          {detail.data?.surveyName ?? `Survey ${surveyId}`}
        </Typography>
      </Box>

      {locked && (
        <Alert severity="info">
          Targeting can only be changed while a survey is still pending. This one is{" "}
          {(detail.data?.status ?? "").toLowerCase()}.
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          <FormControl disabled={!editable}>
            <FormLabel id="target-type-label">Who should receive it</FormLabel>
            <RadioGroup
              row
              aria-labelledby="target-type-label"
              value={targetType}
              onChange={(event) => update({ targetType: event.target.value as TargetType })}
            >
              <FormControlLabel value="ALL" control={<Radio />} label="Every app member" />
              <FormControlLabel value="CRITERIA" control={<Radio />} label="Only those who match" />
            </RadioGroup>
          </FormControl>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Opens"
              type="date"
              size="small"
              value={startDate}
              disabled={!editable}
              onChange={(event) => update({ startDate: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Closes"
              type="date"
              size="small"
              value={endDate}
              disabled={!editable}
              error={endsBeforeStart}
              helperText={endsBeforeStart ? "Closes before it opens" : " "}
              onChange={(event) => update({ endDate: event.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          {targetType === "CRITERIA" && (
            <Stack spacing={3}>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
                {META_FIELDS.filter((field) => isFieldVisible(field.audience, audiences)).map(
                  (field) => multiSelect(field.key, field.label, metas.data?.[field.key] ?? []),
                )}
                {isFieldVisible("laity", audiences) &&
                  multiSelect("gender", "Gender", GENDER_OPTIONS)}
                {multiSelect(
                  "state",
                  "State",
                  (states.data ?? []).map((s) => ({ id: s.id, label: s.state })),
                )}
                {cityOptions.length > 0 &&
                  multiSelect(
                    "city",
                    "City",
                    cityOptions.map((c) => ({ id: c.id, label: c.city })),
                  )}
              </Stack>

              <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
                <TextField
                  label="Age from"
                  type="number"
                  size="small"
                  value={criteria.ageGTE}
                  disabled={!editable}
                  onChange={(event) => setCriterion("ageGTE", event.target.value)}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <TextField
                  label="Age to"
                  type="number"
                  size="small"
                  value={criteria.ageLTE}
                  disabled={!editable}
                  onChange={(event) => setCriterion("ageLTE", event.target.value)}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
              </Stack>

              {criteriaEmpty && (
                <Alert severity="warning">
                  Narrow the audience by at least one criterion, or send it to everyone.
                </Alert>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              disabled={!editable || blocked || save.isPending}
              onClick={onSave}
            >
              Save targeting
            </Button>
            <Button component={RouterLink} to="/dashboard/survey/survey-list">
              Back to surveys
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
